/**
 * Song Question state — pure logic, no Firebase.
 *
 * WHAT THIS IS, AND WHY IT IS NOT A SONG NOTE. A Song Question is written by a
 * FAMILY, between lessons, while a student is practising at home: "I got stuck
 * here", "what did you mean by the breath note?". It waits on the song until
 * whoever teaches next opens the lesson, and the teacher clears it. Anthony,
 * 2026-08-12: "the students don't always remember. Sometimes they come into
 * their lesson and they can't remember what it is they wanted to tell us or ask
 * us to work on."
 *
 * So this is a MEMORY AID FOR THE STUDENT, redeemed in the next lesson. It is
 * NOT a parent-teacher messaging channel and must not grow into one.
 *
 * IT CANNOT BE A SONG NOTE AND CANNOT BE A FIELD ON ONE. core.md is explicit:
 * Song Notes are teacher-write-only; students and parents read, never write.
 * That is enforced in firestore.rules, not merely stated. A shared writable
 * surface on the same doc would undo the wall that makes a `captured` note safe
 * — a teacher records feedback deliberately withheld from the student, and that
 * only holds while the family cannot write there. core.md again: "a visibility
 * boundary is a document boundary." Hence a separate collection with its own
 * rule, which is what this model describes.
 *
 * SCOPED TO (student × song), NEVER TO A LESSON. A question written on Tuesday
 * during practice does not belong to a lesson — there is no lesson yet. It
 * waits on the song. That is also what makes the three-teacher rotation work:
 * whoever teaches next sees it, exactly as with Song Notes' persistence.
 *
 * Kept free of Firestore imports on purpose so the logic is unit-testable
 * without an emulator (songQuestionsModel.test.js). All I/O lives in
 * SongQuestionsService.js — same split as songNotesModel / SongNotesService.
 */

export const SONG_QUESTION_STATUS = {
	OPEN: 'open',
	RESOLVED: 'resolved',
};

export const ALL_QUESTION_STATUSES = [
	SONG_QUESTION_STATUS.OPEN,
	SONG_QUESTION_STATUS.RESOLVED,
];

/**
 * TWO STATES, DELIBERATELY — this does NOT borrow captured/focus/landed.
 *
 * That lifecycle encodes VISIBILITY (`captured` is a wall) and PROGRESS toward a
 * student applying a note. A question has neither property: the family can see
 * it by definition, since they wrote it, and "did they apply it" is meaningless.
 * The only thing worth recording is whether it has been dealt with. Reaching for
 * the richer vocabulary here would imply a wall that does not exist.
 *
 * A resolved state is load-bearing rather than tidy (Anthony, 2026-08-12):
 * without it the pile grows monotonically, teachers stop reading it, and the
 * feature dies quietly while still appearing to work.
 */
export const isOpen = (q) => (q?.status || SONG_QUESTION_STATUS.OPEN) === SONG_QUESTION_STATUS.OPEN;
export const isResolved = (q) => q?.status === SONG_QUESTION_STATUS.RESOLVED;

/**
 * A missing status reads as OPEN, not resolved.
 *
 * Fail-safe direction matters and is the opposite of the Song Note default. A
 * malformed Song Note falls back to `captured` (private) because guessing wrong
 * toward visible would show a family withheld feedback. Here the risk is
 * inverted: guessing "resolved" hides a real question from the teacher, which
 * is the exact failure this feature exists to prevent. Guessing "open" costs a
 * teacher one glance.
 */
export const statusOf = (q) => (isResolved(q) ? SONG_QUESTION_STATUS.RESOLVED : SONG_QUESTION_STATUS.OPEN);

/** Long enough for a real question, short enough that this cannot become a mail client. */
export const QUESTION_MAX_LENGTH = 1000;

/** Collapse runs of whitespace and trim. Newlines survive as single breaks. */
export const normalizeQuestionText = (raw) =>
	String(raw ?? '')
		.replace(/\r\n?/g, '\n')
		.replace(/[ \t]+/g, ' ')
		.replace(/\n{3,}/g, '\n\n')
		.trim();

/**
 * Whether a draft may be submitted. Guards the empty and whitespace-only cases
 * at the same place the length cap lives, so the button state and the write
 * path cannot disagree about what counts as a question.
 */
export const isSubmittable = (raw) => {
	const text = normalizeQuestionText(raw);
	return text.length > 0 && text.length <= QUESTION_MAX_LENGTH;
};

const toMillis = (value) => {
	if (!value) return 0;
	if (typeof value.toMillis === 'function') return value.toMillis();
	if (value.seconds != null) return value.seconds * 1000;
	if (value instanceof Date) return value.getTime();
	const parsed = Date.parse(value);
	return Number.isNaN(parsed) ? 0 : parsed;
};

export const sortByCreatedDesc = (list = []) =>
	[...list].sort((a, b) => toMillis(b?.createdAt) - toMillis(a?.createdAt));

/** Oldest-first. The teacher surface wants the question that has waited longest. */
export const sortByCreatedAsc = (list = []) =>
	[...list].sort((a, b) => toMillis(a?.createdAt) - toMillis(b?.createdAt));

/**
 * Split into the two lists both surfaces render.
 *
 * Open sorts OLDEST-FIRST, which is the opposite of every other list in this
 * codebase and is deliberate: an unanswered question that has survived two
 * lessons is more urgent than one written last night, and newest-first would
 * bury it exactly as the lesson starts. Resolved sorts newest-first, because
 * there it is history and recency is all that matters.
 */
export const partitionQuestions = (list = []) => {
	const open = [];
	const resolved = [];
	for (const q of list) {
		if (!q) continue;
		if (isResolved(q)) resolved.push(q);
		else open.push(q);
	}
	return { open: sortByCreatedAsc(open), resolved: sortByCreatedDesc(resolved) };
};

/** Just the open ones, oldest-first — what the Lesson Dashboard column shows. */
export const openQuestions = (list = []) => partitionQuestions(list).open;

export const openCount = (list = []) => list.filter((q) => q && isOpen(q)).length;

/**
 * Questions attached to one song, for rendering under that song's card.
 *
 * Matches on songId, falling back to a lowercased name comparison for the same
 * reason groupCarryForwardBySong does — a song is not guaranteed to carry a
 * canonical id, and a question about "Defying Gravity" should not detach from
 * the card because one row stored a name and another an id.
 */
export const questionsForSong = (list = [], song = {}) => {
	const id = song.songId || null;
	const name = (song.songName || '').trim().toLowerCase();
	return list.filter((q) => {
		if (!q) return false;
		if (id && q.songId) return q.songId === id;
		const qName = (q.songName || q.songNameLower || '').trim().toLowerCase();
		return Boolean(name) && qName === name;
	});
};

/**
 * Group by song for a standalone list (a student with questions on songs that
 * have no delivered notes yet still needs those questions to appear somewhere).
 */
export const groupQuestionsBySong = (list = []) => {
	const bySong = new Map();
	for (const q of list) {
		if (!q) continue;
		const key = q.songId || (q.songName || '').trim().toLowerCase() || 'unknown';
		if (!bySong.has(key)) {
			bySong.set(key, {
				songId: q.songId || null,
				songName: q.songName || 'Untitled song',
				questions: [],
			});
		}
		bySong.get(key).questions.push(q);
	}
	return [...bySong.values()]
		.map((group) => ({
			...group,
			...partitionQuestions(group.questions),
			total: group.questions.length,
			openTotal: openCount(group.questions),
		}))
		.sort((a, b) => b.openTotal - a.openTotal || b.total - a.total);
};

/**
 * Resolve the Song Note a question was asked ABOUT, by joining in memory
 * against notes the caller already holds.
 *
 * DELIBERATELY NOT DENORMALIZED onto the question. Copying the note's text at
 * write time would go stale the moment a teacher edits it (updateSongNoteText
 * exists and is used), and a quoted question that misquotes the note it refers
 * to is worse than no quote. Both surfaces already load the student's notes, so
 * the join is free.
 *
 * Returns null for a song-level question (no songNoteId) AND for one whose note
 * has since been deleted — callers must render the question either way rather
 * than hiding it, since an orphaned question is still a real question.
 */
export const noteForQuestion = (question, notes = []) => {
	if (!question?.songNoteId) return null;
	return notes.find((n) => n?.id === question.songNoteId) || null;
};

const songQuestionsModel = {
	SONG_QUESTION_STATUS,
	ALL_QUESTION_STATUSES,
	QUESTION_MAX_LENGTH,
	isOpen,
	isResolved,
	statusOf,
	normalizeQuestionText,
	isSubmittable,
	partitionQuestions,
	openQuestions,
	openCount,
	questionsForSong,
	groupQuestionsBySong,
	noteForQuestion,
	sortByCreatedAsc,
	sortByCreatedDesc,
};

export default songQuestionsModel;
